import { useCallback, useMemo } from 'react';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { BonusProposalData, BonusProposalProps } from '~/data/proposals';
import { BonusProgramConfig } from '~/data/rewarding/types';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { useBonusProgramConfig } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BonusProposalForm/useBonusProgramConfig';
import {
  CommonProposalFieldProps,
  EmployeeProposalDecision,
  EmployeeProposalUpdateCallback,
  UseCommonProposalFormOutput,
  transformProposalRequestProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalConfigSelectorProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelector';
import { useCommonFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useCommonFieldProps';
import { useInitiateFormData } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useInitiateFormData';
import { useProposalPermissions } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalPermissions';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';
import { BonusProposalFormProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeBonusProposalsLayout/useBonusProposalsLayout';

import { useEmploymentEnumsFilter } from '../Common/useEmploymentEnumsFilter';
import { useScheduleEmployments } from '../Common/useScheduleEmployments';

import {
  BonusProposalFormData,
  mapDefaultData,
  mapFormDataToLegacyPayload,
  submitShortIncentiveProposal,
  useValidationSchema,
} from './data';

type PropsType = {
  proposal: BonusProposalProps;
  employeeId: number;
  onProposalUpdate: EmployeeProposalUpdateCallback;
  formProps: BonusProposalFormProps;
};

type UseBonusProposalFormOutput =
  UseCommonProposalFormOutput<BonusProposalFormData> & {
    enums: {
      incentiveShortTypes: Enum[];
      // eventFrequencies: Enum[];
    };
    fieldProps: CommonProposalFieldProps;
    bonusProgramConfigSelector: ProposalConfigSelectorProps<BonusProgramConfig>;
    currency: string;
  };

export const useBonusProposalForm = (
  props: PropsType,
): UseBonusProposalFormOutput => {
  const { proposal, employeeId, onProposalUpdate, formProps } = props;
  const { proposalData } = proposal;
  const { bonusProgramConfigs } = formProps;

  const { currency } = useEmployeeDataForProposal({ proposal });

  const { filterActiveEnums } = useEmploymentEnumsFilter();

  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    return {
      incentiveShortTypes: filterActiveEnums(
        getEnums(EnumListKey.INCENTIVE_SHORT_TYPE),
        proposalData?.incentiveShortTypeFK,
      ),
    };
  }, [filterActiveEnums, getEnums, proposalData?.incentiveShortTypeFK]);

  const { defaultConfigType, configSelector: bonusProgramConfigSelector } =
    useBonusProgramConfig({
      proposal,
      bonusProgramConfigs,
      bonusTypes: enums.incentiveShortTypes,
      currency,
    });

  const {
    readOnlyMode,
    userRoles,
    loggedInEmployeeId,
    canSubmitForm,
    responsibleHRSelectEnabled,
  } = useProposalPermissions({ proposal });

  const { hasUpcomingEmployments } = useScheduleEmployments();

  const resolver = useYupValidationResolver(
    useValidationSchema(hasUpcomingEmployments, bonusProgramConfigs),
    'react-hook-form',
  );

  const dataMapper = useCallback(
    (
      targetEmploymentId: number,
      proposalData?: BonusProposalData,
      currentResponsibleHRId?: number,
    ) => {
      return mapDefaultData(
        targetEmploymentId,
        proposalData,
        currentResponsibleHRId,
        defaultConfigType,
      );
    },
    [defaultConfigType],
  );

  const {
    allResponsibleHRIds,
    form,
    handleFormReset,
    isLoading,
    handleSubmitRequest,
  } = useInitiateFormData<BonusProposalProps, BonusProposalFormData>({
    proposal,
    employeeId,
    dataMapper,
    resolver,
  });

  const handleFormSubmit = useCallback(
    (decision?: EmployeeProposalDecision) => {
      return handleSubmitRequest({
        onSuccess: onProposalUpdate,
        handleSubmission: (data, requestProps) => {
          requestProps = transformProposalRequestProps(
            requestProps,
            +data.employmentId,
          );
          return submitShortIncentiveProposal(
            mapFormDataToLegacyPayload({
              data,
              isHRUser: userRoles.isHR,
              loggedInEmployeeId,
              decision,
              requestProps,
            }),
            requestProps,
          );
        },
      });
    },
    [handleSubmitRequest, onProposalUpdate, loggedInEmployeeId, userRoles],
  );

  const { watch } = form;
  const { isLoading: isLoadingCommonFieldProps, ...fieldProps } =
    useCommonFieldProps({
      startDate: { value: watch('paymentPeriodStartDate') },
      endDate: { value: watch('paymentPeriodEndDate') },
      employmentId: watch('employmentId'),
    });

  return {
    form,
    enums,
    bonusProgramConfigSelector,
    handleFormSubmit,
    handleFormReset,
    responsibleHRSelector: {
      options: allResponsibleHRIds,
      isResponsibleHR: userRoles.isResponsibleHR,
      enabled: responsibleHRSelectEnabled,
    },
    isLoading: {
      responsibleHR: isLoading,
      commonFieldProps: isLoadingCommonFieldProps,
    },
    readOnlyMode,
    canSubmitForm,
    fieldProps,
    currency,
  };
};
